import { useRef } from 'react';
import { Dialog } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { AnimatePresence, m as motion } from 'framer-motion';
import type { RegionNew, TripPriceChange } from '@eti/schema-types';
import { usePaymentErrorDataLayerEffect } from '../../../../common/data-layer/ecommerce/custom-hooks/usePaymentErrorDataLayerEffect';
import type { DialogStates } from '../../constants/paymentDialog';
import { dialogStates } from '../../constants/paymentDialog';
import { errorMessageToTextKeyId } from '../../constants/errorMessages';
import type { makeOrderResponseMessages } from '../../constants/makeOrderResponse';
import TransitionContent from './TransitionContent';
import PriceChangeContent, { isPriceIncrease } from './PriceChangeContent';
import PriceChangeSummaryContent from './PriceChangeSummaryContent';
import ErrorContent from './ErrorContent';

const { TRANSITION, PRICE_CHANGE, ERROR } = dialogStates;

const getDialogTitle = (
  dialogState: DialogStates | null,
  priceChanges?: TripPriceChange[],
  errorMessageId?: keyof typeof makeOrderResponseMessages,
  isPriceChangeSummaryEnabled?: boolean,
) => {
  switch (dialogState) {
    case PRICE_CHANGE: {
      if (isPriceChangeSummaryEnabled) {
        return isPriceIncrease(priceChanges ?? [])
          ? 'Payment.PriceChangeDialog.Increase.Title'
          : 'Payment.PriceChangeDialog.Decrease.Title';
      }

      return isPriceIncrease(priceChanges ?? [])
        ? 'Air.Result.ImportantTripInformation.MetaPriceChange.Increase.Title'
        : 'Air.Result.ImportantTripInformation.MetaPriceChange.Decrease.Title';
    }
    case ERROR: {
      return errorMessageId
        ? `PaymentPage.ErrorModal.${errorMessageToTextKeyId[errorMessageId]}.Title`
        : 'PaymentPage.ErrorModal.GenericError.Title';
    }
    case TRANSITION:
    default:
      return 'PaymentPage.PaymentTransitionModal.Title';
  }
};

const motionVariants = {
  parent: {
    enter: { height: 'auto', transition: { type: 'tween', duration: 0.2, when: 'beforeChildren' } },
    exit: { height: 0, transition: { type: 'tween', duration: 0.2 } },
  },
  child: {
    enter: { opacity: 1, transition: { type: 'tween', duration: 0.3 } },
    exit: { opacity: 0, transition: { type: 'tween', duration: 0.15 } },
  },
};

type SelectedPaymentMethod = {
  code: string;
  type: string;
  id: string;
};

interface PaymentDialogProps {
  destination: RegionNew;
  dialogState: DialogStates | null;
  errorMessageId?: keyof typeof makeOrderResponseMessages;
  isPayInternal?: boolean;
  onAcceptPriceChange: (cb?: () => void) => void;
  onCloseError: () => void;
  onDeclinePriceChange: () => void;
  priceChanges?: TripPriceChange[];
  selectedPaymentMethod: SelectedPaymentMethod | null;
  populatePriceChangeDisplayed: (cb: string) => void;
}

const PaymentDialog = ({
  destination,
  dialogState,
  errorMessageId,
  isPayInternal = false,
  onAcceptPriceChange,
  onCloseError,
  onDeclinePriceChange,
  priceChanges,
  selectedPaymentMethod,
  populatePriceChangeDisplayed,
}: PaymentDialogProps) => {
  const transitionFocusRef = useRef<HTMLHeadingElement>(null);
  const { p } = useProperty();
  const { t } = useTranslation();
  const { brand, siteName } = useSiteContext();
  const isPriceChangeSummaryEnabled = p('Payment.PriceChangeDialogSummary.Enabled');
  const dialogTitle = t(
    getDialogTitle(dialogState, priceChanges, errorMessageId, isPriceChangeSummaryEnabled),
  );
  usePaymentErrorDataLayerEffect(dialogState, errorMessageId);

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      data-testid="payment-dialog"
      initialFocusRef={dialogState === TRANSITION ? transitionFocusRef : undefined}
      isOpen={Boolean(dialogState)}
      title={dialogTitle}
    >
      <AnimatePresence initial={false}>
        <motion.div
          animate="enter"
          exit="exit"
          initial="exit"
          key={dialogState}
          variants={motionVariants.parent}
        >
          {dialogState === TRANSITION && (
            <motion.div variants={motionVariants.child}>
              <TransitionContent
                brand={brand.code}
                destination={destination}
                ref={transitionFocusRef}
                siteName={siteName}
              />
            </motion.div>
          )}
          {dialogState === PRICE_CHANGE && priceChanges && (
            <motion.div variants={motionVariants.child}>
              {isPriceChangeSummaryEnabled ? (
                <PriceChangeSummaryContent
                  isPayInternal={isPayInternal}
                  isPriceIncrease={isPriceIncrease(priceChanges)}
                  onAcceptPriceChange={onAcceptPriceChange}
                  onDeclinePriceChange={onDeclinePriceChange}
                  populatePriceChangeDisplayed={populatePriceChangeDisplayed}
                  priceChanges={priceChanges}
                  selectedPaymentMethod={selectedPaymentMethod}
                />
              ) : (
                <PriceChangeContent
                  isPayInternal={isPayInternal}
                  onAcceptPriceChange={onAcceptPriceChange}
                  onDeclinePriceChange={onDeclinePriceChange}
                  populatePriceChangeDisplayed={populatePriceChangeDisplayed}
                  priceChanges={priceChanges}
                  selectedPaymentMethod={selectedPaymentMethod}
                />
              )}
            </motion.div>
          )}
          {dialogState === ERROR && errorMessageId && (
            <motion.div variants={motionVariants.child}>
              <ErrorContent brand={brand.code} messageId={errorMessageId} onClose={onCloseError} />
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>
    </Dialog>
  );
};

export default PaymentDialog;
